import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  InputAdornment,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  AdminPanelSettings as AdminIcon,
  Work as TherapistIcon,
} from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

import { useAuth } from '../context/AuthContext';
import { userService, User, UserCreate, UserUpdate } from '../services/userService';
import { tenantService, Tenant } from '../services/tenantService';

const UsersPage: React.FC = () => {
  const { user: currentUser } = useAuth();

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  // UI State
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const { register, handleSubmit, reset, watch, formState: { errors } } = useForm<UserCreate & UserUpdate & { confirmPassword?: string }>();

  const watchRole = watch('role');

  // Filter users based on search query
  const filteredUsers = users.filter(user => {
    const query = searchQuery.toLowerCase();
    return (
      user.full_name.toLowerCase().includes(query) ||
      user.email.toLowerCase().includes(query) ||
      user.role.toLowerCase().includes(query)
    );
  });

  // Load data
  useEffect(() => {
    const loadUsersData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [usersData, tenantsData] = await Promise.all([
          userService.getUsers(),
          tenantService.getTenants(),
        ]);

        setUsers(usersData);
        setTenants(tenantsData);
      } catch (err) {
        console.error('Error loading users data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load users data');
      } finally {
        setLoading(false);
      }
    };

    if (currentUser?.role === 'admin') {
      loadUsersData();
    }
  }, [currentUser]);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, user: User) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUser(null);
  };

  const handleEdit = () => {
    if (selectedUser) {
      setEditingUser(selectedUser);
      reset({
        email: selectedUser.email,
        first_name: selectedUser.first_name,
        last_name: selectedUser.last_name,
        role: selectedUser.role,
        tenant_id: selectedUser.tenant_id || undefined,
        phone: selectedUser.phone || '',
        license_number: selectedUser.license_number || '',
        is_active: selectedUser.is_active,
      });
      setAddDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleDelete = () => {
    if (selectedUser) {
      setDeleteDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleAddUser = () => {
    setEditingUser(null);
    reset({
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      role: 'therapist',
      tenant_id: undefined,
      phone: '',
      license_number: '',
      is_active: true,
    });
    setAddDialogOpen(true);
  };

  const onSubmit = async (data: UserCreate & UserUpdate & { confirmPassword?: string }) => {
    try {
      const { confirmPassword, is_active, ...userData } = data;

      if (!editingUser && data.password !== confirmPassword) {
        toast.error('Passwords do not match');
        return;
      }

      if (editingUser) {
        // Update existing user
        const { password, ...updateData } = userData;
        // Include is_active for updates
        const updateDataWithStatus = { ...updateData, is_active };
        const updatedUser = await userService.updateUser(editingUser.id, updateDataWithStatus);
        setUsers(users.map(u => u.id === editingUser.id ? updatedUser : u));
        toast.success('User updated successfully');
      } else {
        // Create new user (is_active not needed for creation as it defaults to true)
        const newUser = await userService.createUser(userData as UserCreate);
        setUsers([...users, newUser]);
        toast.success('User created successfully');
      }

      setAddDialogOpen(false);
      setEditingUser(null);
      reset();
    } catch (error: any) {
      console.error('Error saving user:', error);
      toast.error(error.response?.data?.detail || 'Failed to save user');
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedUser) return;

    try {
      await userService.deleteUser(selectedUser.id);
      setUsers(users.filter(u => u.id !== selectedUser.id));
      toast.success('User deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedUser(null);
    } catch (error: any) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const getRoleIcon = (role: string) => {
    return role === 'admin' ? <AdminIcon /> : <TherapistIcon />;
  };

  const getRoleColor = (role: string) => {
    return role === 'admin' ? 'error' : 'primary';
  };

  if (currentUser?.role !== 'admin') {
    return (
      <Alert severity="error">
        You don't have permission to access this page.
      </Alert>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        Error loading users: {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          User Management ({filteredUsers.length}{users.length !== filteredUsers.length ? ` of ${users.length}` : ''})
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddUser}
        >
          Add User
        </Button>
      </Box>

      <Box mb={3}>
        <TextField
          fullWidth
          placeholder="Search users by name, email, or role..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Tenant</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>2FA</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.map((user) => (
              <TableRow key={user.id}>
                <TableCell>
                  <Box display="flex" alignItems="center">
                    <PersonIcon sx={{ mr: 2, color: 'text.secondary' }} />
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {user.full_name}
                      </Typography>
                      {user.license_number && (
                        <Typography variant="caption" color="text.secondary">
                          License: {user.license_number}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <Chip
                    icon={getRoleIcon(user.role)}
                    label={user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                    color={getRoleColor(user.role) as any}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {user.tenant_id ? `Tenant ${user.tenant_id}` : 'All Tenants'}
                </TableCell>
                <TableCell>{user.phone || 'N/A'}</TableCell>
                <TableCell>
                  <Chip
                    label={user.is_active ? 'Active' : 'Inactive'}
                    color={user.is_active ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={user.is_2fa_enabled ? 'Enabled' : 'Disabled'}
                    color={user.is_2fa_enabled ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    onClick={(e) => handleMenuClick(e, user)}
                    disabled={user.id === currentUser.id}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEdit}>
          <EditIcon sx={{ mr: 2 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 2 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Add/Edit User Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>
            {editingUser ? 'Edit User' : 'Add New User'}
          </DialogTitle>
          <DialogContent>
            <Box display="flex" gap={2}>
              <TextField
                {...register('first_name', { required: 'First name is required' })}
                fullWidth
                label="First Name"
                margin="normal"
                error={!!errors.first_name}
                helperText={errors.first_name?.message as string}
              />
              <TextField
                {...register('last_name', { required: 'Last name is required' })}
                fullWidth
                label="Last Name"
                margin="normal"
                error={!!errors.last_name}
                helperText={errors.last_name?.message as string}
              />
            </Box>

            <TextField
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address'
                }
              })}
              fullWidth
              label="Email"
              type="email"
              margin="normal"
              error={!!errors.email}
              helperText={errors.email?.message as string}
            />

            {!editingUser && (
              <>
                <TextField
                  {...register('password', {
                    required: 'Password is required',
                    minLength: { value: 6, message: 'Password must be at least 6 characters' },
                    maxLength: { value: 72, message: 'Password cannot be longer than 72 characters' }
                  })}
                  fullWidth
                  label="Password"
                  type="password"
                  margin="normal"
                  error={!!errors.password}
                  helperText={errors.password?.message as string}
                />
                <TextField
                  {...register('confirmPassword', {
                    required: 'Please confirm password'
                  })}
                  fullWidth
                  label="Confirm Password"
                  type="password"
                  margin="normal"
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword?.message as string}
                />
              </>
            )}

            <Box display="flex" gap={2}>
              <FormControl fullWidth margin="normal" error={!!errors.role}>
                <InputLabel>Role</InputLabel>
                <Select
                  {...register('role', { required: 'Role is required' })}
                  label="Role"
                  defaultValue="therapist"
                >
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="therapist">Therapist</MenuItem>
                </Select>
              </FormControl>

              {watchRole === 'therapist' && (
                <FormControl fullWidth margin="normal">
                  <InputLabel>Tenant</InputLabel>
                  <Select
                    {...register('tenant_id', { valueAsNumber: true })}
                    label="Tenant"
                  >
                    <MenuItem value="">None</MenuItem>
                    {tenants.map((tenant) => (
                      <MenuItem key={tenant.id} value={tenant.id}>
                        {tenant.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>

            <Box display="flex" gap={2}>
              <TextField
                {...register('phone')}
                fullWidth
                label="Phone"
                margin="normal"
              />
              <TextField
                {...register('license_number')}
                fullWidth
                label="License Number"
                margin="normal"
                disabled={watchRole !== 'therapist'}
                helperText={watchRole !== 'therapist' ? 'Only for therapists' : ''}
              />
            </Box>

            {editingUser && (
              <FormControlLabel
                control={
                  <Switch
                    {...register('is_active')}
                    defaultChecked={editingUser.is_active}
                  />
                }
                label="Active User"
                sx={{ mt: 2 }}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="contained">
              {editingUser ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete user "{selectedUser?.full_name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UsersPage;